import { RefObject, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useFolder } from "src/core/contexts/FolderContext";
import {
  Diligence,
  useCreateDiligencesMutation,
  useUpdateDiligenceMutation,
} from "src/core/gql/graphql";
import { useToast } from "src/core/hooks/use-toast";
import { mapCodeToCustomStatus } from "src/core/utils/status";

export const useFolderDiligences = (diligencesListRef: RefObject<HTMLElement>) => {
  const [createDiligences] = useCreateDiligencesMutation();
  const [updateDiligence] = useUpdateDiligenceMutation();
  const [openUpsertModal, setOpenUpsertModal] = useState<boolean>(false);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [diligenceToEdit, setDiligenceToEdit] = useState<Diligence | null>(null);
  const { toast } = useToast();
  const { t } = useTranslation();
  const {
    folderFile,
    tabCounters,
    refetch,
    isDiligencesPrepared,
    isReadOnly,
    setIsDiligencesCreated,
    isDiligencesCreated,
  } = useFolder();
  const folderState = useMemo(
    () => mapCodeToCustomStatus(folderFile.folderFile?.workflowStatus?.state?.code),
    [folderFile.folderFile?.workflowStatus?.state?.code]
  );
  const [isDilgencesClicked, setIsDilgencesClicked] = useState<boolean>(false);
  const [isDiligenceReady, setIsDiligenceReady] = useState<boolean>(
    tabCounters.global === 0 && !isDiligencesPrepared
  );
  const [loadingDiligenceId, setLoadingDiligenceId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const diligences = useMemo(() => folderFile?.folderFile?.diligences || [], [folderFile]);

  useEffect(() => {
    if (tabCounters.global === 0 && !isDiligencesPrepared) {
      setIsDiligenceReady(true);
    } else {
      setIsDiligenceReady(false);
    }
  }, [tabCounters.global, isDiligencesPrepared]);

  const prepareDiligence = useCallback(async () => {
    if (!isDilgencesClicked && !isDiligenceReady) setIsDilgencesClicked(true);
    if (isDiligenceReady && folderFile?.folderFile?.id) {
      setIsLoading(true);
      try {
        await createDiligences({ variables: { folderFileId: folderFile?.folderFile?.id } });
        await refetch();
        setIsDiligencesCreated(true);
        toast({
          description: t("folder.diligences.diligencePrepared"),
        });
        // Scroll vers la liste des diligences après le refetch
        setTimeout(() => {
          if (diligencesListRef?.current) {
            diligencesListRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }, 100);
      } catch (error) {
        console.error(error);
        toast({
          description: t("folder.diligences.diligencePrepareError"),
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    }
  }, [
    isDilgencesClicked,
    folderFile?.folderFile?.id,
    createDiligences,
    refetch,
    toast,
    t,
    diligencesListRef,
    isDiligenceReady,
    setIsDiligencesCreated,
  ]);

  const handleDiligenceChange = useCallback(
    async (diligenceId: string, name: string, checked: boolean) => {
      setLoadingDiligenceId(diligenceId);
      try {
        await updateDiligence({ variables: { diligenceId, name, selected: checked } });
        await refetch();
      } catch (error) {
        console.error(error);
        toast({
          description: t("folder.diligences.diligenceChangeError"),
          variant: "destructive",
        });
      } finally {
        setLoadingDiligenceId(null);
      }
    },
    [updateDiligence, refetch, toast, t]
  );

  const handleEditDiligence = useCallback((diligence: Diligence) => {
    setOpenUpsertModal(true);
    setDiligenceToEdit(diligence);
  }, []);

  const handleDeleteDiligence = useCallback((diligence: Diligence) => {
    setOpenDeleteModal(true);
    setDiligenceToEdit(diligence);
  }, []);

  const handleAddDiligence = useCallback(async () => {
    setDiligenceToEdit(null);
    setOpenUpsertModal(true);
  }, []);

  return {
    diligences,
    isDilgencesClicked,
    prepareDiligence,
    handleDiligenceChange,
    handleEditDiligence,
    handleDeleteDiligence,
    handleAddDiligence,
    loadingDiligenceId,
    isLoading,
    isReadOnly,
    isDiligenceReady,
    openUpsertModal,
    setOpenUpsertModal,
    diligenceToEdit,
    setDiligenceToEdit,
    openDeleteModal,
    setOpenDeleteModal,
    isDiligencesCreated,
    folderState,
  };
};
